import React from 'react';
import {
  AreaChart, Area, LineChart, Line, XAxis, YAxis, CartesianGrid, Tooltip, Legend, ResponsiveContainer
} from 'recharts';
import type { WidgetProps } from '../types';
import { WidgetDataGuard } from '../components/WidgetDataGuard';
import { ChartTooltip } from '../components/ChartTooltip';
import { resolveLabel } from '../utils/resolveChartFields';
import { useChartData } from '../hooks/useChartData';
import { useDashboardTokens } from '../utils/dashboardTokens';
import { colorForSeries } from '../utils/chartColors';
import { formatCompact, resolveCurrency } from '../utils/formatNumber';
import { readFormat } from '../utils/widgetConfig';

/**
 * Líneas — para evolución temporal.
 *
 * Cuatro cambios sobre la versión anterior, todos en la misma dirección: que se vea
 * la FORMA de la serie y no su decoración.
 *
 *  - <b>Trazo de 2px y sin puntos en reposo.</b> Antes iba a 3px con un `dot` de
 *    radio 4 en CADA punto: con veinte fechas, la línea desaparecía bajo veinte
 *    círculos. El punto ahora sale sólo bajo el cursor.
 *  - <b>Área rellena, pero sólo con una serie.</b> Con dos o más, los rellenos se
 *    superponen y ninguna se lee.
 *  - <b>Sin grilla vertical</b> y con el eje Y abreviado.
 *  - <b>Los huecos interrumpen la línea.</b> `connectNulls` está en `false` a
 *    propósito: interpolar sobre un dato ausente dibuja una tendencia que nadie
 *    midió.
 */
const ChartLine: React.FC<WidgetProps> = ({ definition, period, preference }) => {
  const { chartData, valueKeys, categoryField, aliases, data, loading, error } =
    useChartData(definition, preference, period);
  const tokens = useDashboardTokens();

  // `readFormat` valida contra la unión: un `format` que no existe cae a `undefined` en vez de
  // colarse como una cadena cualquiera hasta el formateo.
  const currency = readFormat(definition.config_schema) === 'currency' ? resolveCurrency().symbol : null;

  const isSingleSeries = valueKeys.length === 1;
  // La serie primaria lleva el acento de marca; las demás, la paleta categórica SIN el gris
  // del último índice, que está reservado para el grupo "Otros".
  const colorFor = (index: number) => (index === 0 ? tokens.accent : colorForSeries(index));

  const axes = (
    <>
      <CartesianGrid strokeDasharray="3 3" vertical={false} stroke={tokens.border} />
      <XAxis
        dataKey={categoryField}
        axisLine={false}
        tickLine={false}
        tick={{ fill: tokens['text-muted'], fontSize: 11 }}
        dy={8}
      />
      <YAxis
        axisLine={false}
        tickLine={false}
        tick={{ fill: tokens['text-muted'], fontSize: 11 }}
        tickFormatter={(value: number) => formatCompact(value, { currency })}
        width={56}
      />
      <Tooltip
        cursor={{ stroke: tokens['border-strong'], strokeWidth: 1 }}
        content={<ChartTooltip currency={currency} />}
      />
      {!isSingleSeries && (
        <Legend wrapperStyle={{ paddingTop: 8, fontSize: 12, color: tokens['text-secondary'] }} />
      )}
    </>
  );

  return (
    <WidgetDataGuard loading={loading} error={error} data={data} shape="line">
      <div className="w-full h-full px-2 pb-4">
        <ResponsiveContainer width="100%" height="100%">
          {isSingleSeries ? (
            <AreaChart data={chartData} margin={{ top: 8, right: 8, left: -18, bottom: 0 }}>
              {axes}
              <Area
                type="monotone"
                dataKey={valueKeys[0]}
                name={resolveLabel(valueKeys[0], aliases)}
                stroke={tokens.accent}
                strokeWidth={2}
                strokeLinejoin="round"
                fill={tokens.accent}
                fillOpacity={0.08}
                dot={false}
                activeDot={{ r: 3, strokeWidth: 0 }}
                connectNulls={false}
              />
            </AreaChart>
          ) : (
            <LineChart data={chartData} margin={{ top: 8, right: 8, left: -18, bottom: 0 }}>
              {axes}
              {valueKeys.map((key, i) => (
                <Line
                  key={key}
                  type="monotone"
                  dataKey={key}
                  name={resolveLabel(key, aliases)}
                  stroke={colorFor(i)}
                  strokeWidth={2}
                  strokeLinejoin="round"
                  dot={false}
                  activeDot={{ r: 3, strokeWidth: 0 }}
                  connectNulls={false}
                />
              ))}
            </LineChart>
          )}
        </ResponsiveContainer>
      </div>
    </WidgetDataGuard>
  );
};

export default ChartLine;
